// @ts-strict-ignore
/* global DEBUG_LOG */

// mixpanel-specific report utils

import queryString from 'query-string';
import range from 'lodash/range';
import isEqual from 'lodash/isEqual';
import JSURL from 'jsurl';
import last from 'lodash/last';

import {UrlManager} from 'common/contexts/url-manager/types';
import {WindowUrlManager} from 'common/contexts/url-manager/window';
import {htmlEncodeString} from 'common/util';
import {isVerifiedDomain, pluralize} from 'common/util/string';
import {RelativeDateRange} from 'common/widgets/date-range-picker/models/relative-date-range';
import {Section} from 'common/components/items-menu/types';
import {ComparisonDate} from 'common/query-builder/query-block/time-comparison-menu/types';
import type {QueryEventEntry} from 'common/query-builder/query-entry-section/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS, PresetDateRange} from 'common/widgets/date-range-picker/models/presets';
import type {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import type {Event} from 'common/query-builder/query-block/events-menu/types';
import {getLocation} from 'common/util/url';
import type {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';
import IRON_APPS_INFO from 'iron/spa-apps.json';
import {MATH_TYPE, PRIMITIVE_MATH_TYPES} from 'common/types/reports/bookmark';
import {DataVolumeAnomaly} from 'apps/lexicon/src/models';

import {
  CSV_EXPORT_CHOICES_PROJECT_OVERRIDES,
  DownloadMode,
  INSIGHTS_AGGREGATE_CSV_EXPORT_CHOICES,
  INTERNAL_PROJECTS,
  LocalStorageKey,
  PERSISTED_QUERY_PARAMS_ALLOW_SET,
  PRODUCTION_DOMAINS,
  PropertyType,
  ReportName,
  RESIDENCY_CLUSTER_IDS_BY_COUNTRY_CODE,
  ResourceType,
} from '../constants';
import {reporter} from '../error-reporting';
import {Persistence} from '../persistence';
import {BaseQueryResultsType, BaseQueryResultsWithMetaType, ResidencyClusterCountryCode} from '../types';
import {isStandalone} from './env';
import {makePathFromUrlState, StateFromUrlUtils, UrlFormat} from './state-from-url';

const defaultUrlManager = new WindowUrlManager();

export type IronAppInfo = {
  appName: string;
  title?: string;
  internalReportName: string;
  isSpa?: boolean;
};

export enum AnomalyTypes {
  Event = `event`,
  Metric = `metric`,
  Property = `property`,
  PropertyTypeDrift = `property_type_drift`,
}

export const commonReportUtils = {
  downloadLocalCSVData,
};

/** This is being used by spa-nav-manager to dynamically load bundles for volt mode */
export const urlToIronAppInfo: {[url: string]: IronAppInfo} = {};
Object.entries(IRON_APPS_INFO).forEach(
  ([appName, appInfo]) => (urlToIronAppInfo[appName] = {appName, isSpa: true, ...appInfo}),
);

const NON_PERSISTABLE_REPORTS = new Set<ReportName>([
  ReportName.Settings,
  ReportName.Setup,
  // views is not persisted because we can't jump from
  // a project with workspaces to a project without without workspaces
  // we let django handle the correct routing
  ReportName.Views,
]);

export function getPropertyTypeForMathType(type: MATH_TYPE, propertyType?: PropertyType): PropertyType {
  if (
    propertyType !== PropertyType.Number &&
    (type === PRIMITIVE_MATH_TYPES.MOST_FREQUENT || type === PRIMITIVE_MATH_TYPES.FIRST_VALUE)
  ) {
    return PropertyType.String;
  }

  return PropertyType.Number;
}

export function isPersistableReport(): boolean {
  const reportName = getReportName();
  return Boolean(reportName) && !NON_PERSISTABLE_REPORTS.has(reportName);
}

export function isVolt(): boolean {
  return StateFromUrlUtils.parseStateFromUrl()?.urlFormat === UrlFormat.Volt;
}

export function closestReportLink(target: HTMLElement): HTMLAnchorElement {
  return target.closest(isVolt() ? `a[href^='/project/']` : `a[href^='/report/']`);
}

/**
 * Some of the app uses react router's path router
 * e.g. /metric-tree/<canvasId> or /lexicon/<entity>/<lexiconId>
 */
const PATH_BASED_APPS = [`metric-tree`, `experiments`, `lexicon`, `feature-flags`, `heatmaps`];
export function isPathBasedApp(app: string) {
  return PATH_BASED_APPS.includes(app);
}

export function getPersistedSearch(search: string): string {
  const searchParams = getPersistedSearchParams(search);
  return searchParams.size > 0 ? `?${searchParams.toString()}` : ``;
}

export function getPersistedSearchParams(search: string): URLSearchParams {
  const searchParams = new URLSearchParams();
  for (const [key, value] of new URLSearchParams(search)) {
    if (PERSISTED_QUERY_PARAMS_ALLOW_SET.has(key)) {
      searchParams.set(key, value);
    }
  }
  return searchParams;
}

/**
 * Builds a report URL for navigation, accounting for volt and non-volt url schemes.
 *
 * @param endpoint - endpoint to navigate to, e.g. /insights, /settings, /boards. If null, uses current endpoint.
 * @returns - path and hash string of the report url
 */
export function getReportUrl(
  endpoint: string,
  {projectId, workspaceId}: {projectId: string | number; workspaceId?: string | number},
  ignoreSearch: boolean = false,
  queryParams: {[key: string]: string} = {},
): string {
  const [, appName, hash = ``] = /^\/?([^#/]+)\/?(#.*)?/.exec(endpoint);

  // SPA apps and volt app shares same pathname
  const useVolt = isVolt() && appName in IRON_APPS_INFO;
  const path = makePathFromUrlState({
    urlFormat: useVolt ? UrlFormat.Volt : UrlFormat.Django,
    projectId: projectId ? String(projectId) : ``,
    workspaceId: workspaceId ? String(workspaceId) : ``,
    appName: isPathBasedApp(appName) ? endpoint : appName,
  });

  let searchParams: URLSearchParams;
  if (!ignoreSearch) {
    searchParams = getPersistedSearchParams(getLocation().search);
  } else {
    searchParams = new URLSearchParams();
  }

  Object.keys(queryParams).forEach((key) => {
    searchParams.set(key, queryParams[key]);
  });

  const search = searchParams.size > 0 ? `?${searchParams.toString()}` : ``;

  return `${path}${search}${hash}`;
}

export function getEventsReportUrlForEvent({
  projectId,
  workspaceId,
  event,
  presetDateRange = PresetDateRange.Last7Days,
}: {
  projectId: string | number;
  workspaceId?: string | number;
  event: Event;
  presetDateRange?: PresetDateRange;
}) {
  const entries: QueryEventEntry[] = [
    {
      type: ResourceType.Event,
      event,
      filters: [],
    },
  ];

  return getEventsReportUrl({
    projectId,
    workspaceId,
    entries,
    dateRange: new DateRange(PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[presetDateRange]),
    filters: [],
  });
}

export function getEventsReportUrl({
  projectId,
  workspaceId,
  entries,
  dateRange,
  filters,
}: {
  projectId: string | number;
  workspaceId?: string | number;
  entries: QueryEventEntry[];
  dateRange: DateRange;
  filters: PropertyFilter[];
}) {
  return (
    getReportUrl(`events`, {
      projectId,
      workspaceId,
    }) + `#${JSURL.stringify({entries, dateRange, filters})}`
  );
}

export function getRCAReportUrl({
  anomaly,
  projectId,
  workspaceId,
}: {
  anomaly: DataVolumeAnomaly;
  projectId: string | number;
  workspaceId: string | number;
}): string {
  const anomalyTimestamp = anomaly.timestamp.toISOString().split(`T`)[0];
  const urlHash =
    anomaly.anomalyClass === AnomalyTypes.Metric
      ? `rca=${anomaly.metricId}&date=${anomalyTimestamp}&type=issues-hub&metric_source=true`
      : `rca=${anomaly.eventId}&date=${anomalyTimestamp}&type=issues-hub`;
  return `${getReportUrl(`boards`, {
    projectId,
    workspaceId,
  })}#${urlHash}`;
}

export function removeForcedProjectId() {
  delete window[`__mpForceProjectId`];
}
export function forceProjectId(projectId) {
  window[`__mpForceProjectId`] = projectId;
}

function isNumericId(input: string) {
  return /^\d+$/.test(input) && Number.isFinite(Number.parseInt(input, 10));
}

/**
 * fetch current project ID, either from:
 * - URL path (/project/<id>/)
 * - mp.report.globals (when not present on url)
 */
export function getProjectId(_urlManager: UrlManager = null): string {
  // TODO temporary escape hatch here for situations where we need to force
  // a specific project ID that can't be derived by other means
  // In the future this should be replaced by deriving project ID (and project details)
  // from a Panel Context rather than this global helper function.
  if (`__mpForceProjectId` in window) {
    return window[`__mpForceProjectId`] as string;
  }

  // 99% of time, urls will contain projectId e.g /report/<pid>/<appName> or /report/<pid>/view/<wid>
  const location = getLocation();
  const urlState = StateFromUrlUtils.parseStateFromUrl(location.pathname);

  let projectId;

  if (urlState?.projectId) {
    projectId = urlState.projectId;
  } else if (isStandalone()) {
    // dashboard-digest urls look like https://mixpanel.com/dashboard-digest/?project_id=1495621#access_token=...
    // see https://github.com/mixpanel/analytics/blob/84bd2753540824f610613e30c82e6adbe8d4fc99/email_digests/dashboards/dashboard_digest.py#L147
    // so we need parse from url params
    projectId = getQueryStringParam(location, `project_id`) || ``;
  } else {
    // project_id provided by django template is our last resort
    // if projectId isn't in url e.g some settings urls
    // @ts-ignore
    projectId = String(window.mp.report?.globals?.project_id || ``);
  }

  return isNumericId(projectId) ? projectId : ``;
}

export function getWorkspaceId(urlManager: UrlManager = null): string {
  const location = urlManager?.getLocation() || getLocation();
  const urlState = StateFromUrlUtils.parseStateFromUrl(location.pathname);

  let workspaceId;
  if (urlState?.workspaceId) {
    workspaceId = urlState.workspaceId;
  } else if (isStandalone()) {
    // dashboard-digest urls look like https://mixpanel.com/dashboard-digest/?project_id=1495621#access_token=...
    // see https://github.com/mixpanel/analytics/blob/84bd2753540824f610613e30c82e6adbe8d4fc99/email_digests/dashboards/dashboard_digest.py#L147
    // so we need parse from url params
    workspaceId = queryString.parse(location.search).workspace_id || ``;
  } else {
    // workspace_id provided by django template is our last resort
    // @ts-ignore
    workspaceId = String(window.mp.report?.globals?.workspace_id || ``);
  }

  return isNumericId(workspaceId) ? workspaceId : ``;
}

/**
 * @returns the current app name from url e.g /report/x/(insights) => ReportName.Insights
 *
 * Mind that this function might return names other than the report, for example
 * an insights report inside a dashboard would get `ReportName.Dashboards`.
 * If you need the type of report you're looking at regardless of where that report is,
 * do not use this function.
 */
export function getReportName(ironAppName?: string): ReportName {
  ironAppName = ironAppName ?? StateFromUrlUtils.parseStateFromUrl()?.appName;
  const reportName = IRON_APPS_INFO[ironAppName]?.internalReportName as ReportName;

  return reportName ?? null;
}

/**
 * Creates a report url for the given app name. The result of this function is NOT safe for subpath string concatenation.
 *
 * @param appName
 * @returns Stringified URL including path, search param, and hash
 */
export function getAppUrl(appName: string) {
  return getReportUrl(appName, {
    projectId: getProjectId(),
    workspaceId: getWorkspaceId(),
  });
}

export function getProjectSetupUrl() {
  return getReportUrl(`setup/`, {
    projectId: getProjectId(),
    workspaceId: getWorkspaceId(),
  });
}

export async function moveReport(dashboardId, projectId, ev, dashboardsClient) {
  await dashboardsClient.updateDashboardFields({
    projectId,
    dashboardID: dashboardId,
    fields: {
      content: {
        action: ev.detail.actionType,
        content_id: ev.detail.contentId,
        content_params: ev.detail.contentParams,
        content_type: ev.detail.contentType,
      },
    },
  });

  const targetDashboard = dashboardsClient.querySingle({
    projectId,
    dashboardID: ev.detail.contentParams.target_dashboard_id,
  });

  return targetDashboard.network;
}

/*
 * downloadData()
 *
 * Initiates a direct download to the browser for any HTTP method,
 * using a terrible hack of submitting a hidden form to a hidden
 * iframe target. This hack is needed because the result may be too
 * large to fit into the client's memory before triggering the download.
 *
 */
let downloadIdCounter = 1;
export function downloadData(endpoint, params, onErrorCallback: (message: string) => void = null, method = `POST`) {
  // prepare target iframe
  const exportFrameID = `download-frame-${downloadIdCounter++}`;
  const exportFrame = document.createElement(`iframe`);
  exportFrame.id = exportFrameID;
  exportFrame.name = exportFrameID;
  exportFrame.src = ``;
  exportFrame.style.display = `none`;
  document.body.appendChild(exportFrame);

  // prepare source form
  const postForm = document.createElement(`form`);
  postForm.action = endpoint;
  postForm.method = method;
  postForm.autocomplete = `off`;
  postForm.style.display = `none`;
  postForm.target = exportFrameID;
  // This is only used in jql-console and we control all the values coming in here
  // eslint-disable-next-line no-unsanitized/property
  postForm.innerHTML = Object.keys(params)
    .map((p) => `<input autocomplete="off" type="hidden" name="${p}" value="${htmlEncodeString(params[p])}"/>`)
    .join(``);

  // submit request
  document.body.appendChild(postForm);
  postForm.submit();

  if (onErrorCallback) {
    // When the download is successful the iframe doesn't emit any events. When the export fails
    // the body of the iframe is a json response that can be parsed
    exportFrame.addEventListener(
      `load`,
      () => {
        const iframeResponse = exportFrame.contentDocument.body.innerText;
        const errorMessage = `An error occurred creating export`;
        let errorDescription;

        // the body should always be a failed json payload, but guard against it being empty or otherwise malformed
        try {
          const errorResponse = JSON.parse(iframeResponse);
          reporter.error(errorMessage, errorResponse);
          [, errorDescription] = errorResponse.error.split(`desc = `);
        } catch {
          reporter.error(errorMessage, iframeResponse);
        }

        onErrorCallback(errorDescription ?? errorMessage);
      },
      {
        once: true,
      },
    );
  }
}

/**
 * Downloads a file to the user's machine.
 * @param blob - representation of data to be downloaded
 * @param title - title of file to be downloaded
 * @param format - format of file to be downloaded (e.g. PDF/PNG)
 */
export function downloadBlobAsFile(blob: Blob, title: string, format?: string) {
  const blobURL = URL.createObjectURL(blob);
  // launch named download via hidden link
  const link = document.createElement(`a`);
  link.style.display = `none`;
  link.href = blobURL;
  link.download = format ? `${title}.${format.toLowerCase()}` : title;
  document.body.appendChild(link);
  link.click();
  // clean up
  URL.revokeObjectURL(blobURL);
  link.remove();
}

function downloadLocalCSVData(filename, dataStr) {
  const link = document.createElement(`a`);
  const downloadAttrSupported = `download` in link;
  link.remove();
  let blobSize = 0;
  if (downloadAttrSupported) {
    // prepare blob
    const blob = new Blob([dataStr], {type: `text/csv;charset=utf-8`});
    blobSize = blob.size;
    downloadBlobCSVData(filename, blob);
  } else {
    // Work-around for Safari. One drawback of this method is that it doesn't support setting a filename.
    // On Safari, it downloads the file with the name 'Unknown'
    window.open(`data:attachment/csv;charset=utf-8,` + encodeURIComponent(dataStr));
  }

  return {blobSize};
}

export function downloadBlobCSVData(filename: string, blob) {
  // launch named download via hidden link
  const link = document.createElement(`a`);

  const blobURL = URL.createObjectURL(blob);

  link.style.display = `none`;
  link.href = blobURL;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  // clean up
  URL.revokeObjectURL(blobURL);
  link.remove();
}

export function downloadCSVData(url) {
  // launch named download via hidden link
  const link = document.createElement(`a`);
  link.style.display = `none`;
  link.href = url;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// TODO epurcer - replace this with a more general-purpose tool like https://www.npmjs.com/package/debug
function getLogger(level) {
  if (typeof DEBUG_LOG !== `undefined` && DEBUG_LOG) {
    /* eslint-disable no-console */
    return function () {
      console[level](...arguments); // eslint-disable-line prefer-rest-params
    };
    /* eslint-enable no-console */
  }
  return function () {};
}

interface Logger {
  [level: string]: (...args: any) => void;
}

export const debug: Logger = [`log`, `info`, `warn`, `error`, `critical`].reduce(
  (ret, level) => ({...ret, [level]: getLogger(level)}),
  {},
);

/**
 * Rate limit reporting in prod, but always log this warning in dev.
 */
export function warnDeveloper(...args: any): void {
  debug.warn(...args);
  if (Math.random() < 0.01) {
    if (args.length === 1) {
      reporter.warn(args[0]);
    } else if (args.length === 2) {
      reporter.warn(args[0], args[1]);
    } else if (args.length > 2) {
      const [message, info, ...rest] = args;
      reporter.warn(message, info, ...rest);
    }
  }
}

export function formatMaxHistoryDays(maxHistoryDays) {
  if (maxHistoryDays < 365) {
    return `${maxHistoryDays} days`;
  }
  const years = Math.floor(maxHistoryDays / 365);
  return `${years} ${pluralize(`year`, years)}`;
}

export function excludeDemos<T extends {isDemo: boolean}>(orgsOrProjects: T[]): T[] {
  return orgsOrProjects.filter((orgOrProject) => !orgOrProject.isDemo);
}

export function getDownloadMode(urlManager: UrlManager = defaultUrlManager): DownloadMode {
  if (urlManager.getPath().match(/\/embed\/?$/)) {
    return DownloadMode.File;
  }
  return Number.parseInt(urlManager.getQueryStringParam(`download`), 10) || DownloadMode.None;
}

export function getDownloadModeFromLocation(): DownloadMode {
  const location = getLocation();
  if (location.pathname.match(/\/embed\/?$/)) {
    return DownloadMode.File;
  }
  return Number.parseInt(getQueryStringParam(location, `download`), 10) || DownloadMode.None;
}

export function getDownloadDisplayPreferences(urlManager: UrlManager = defaultUrlManager): Obj {
  try {
    return JSON.parse(urlManager.getQueryStringParam(`display_preferences`)) || {};
  } catch {
    return {};
  }
}

export function isPublicMode(urlManager: UrlManager = defaultUrlManager): boolean {
  return urlManager.getPath().startsWith(`/public/`);
}

export function isAuthMode(urlManager: UrlManager = defaultUrlManager): boolean {
  const AUTH_PATHS = [`/login`];
  return AUTH_PATHS.some((path) => urlManager.getPath().startsWith(path));
}

export function isEmbedMode(urlManager: UrlManager = defaultUrlManager): boolean {
  return !!urlManager.getPath().match(/\/embed\/?$/);
}

export function isCheckoutMode(urlManager: UrlManager = defaultUrlManager): boolean {
  return urlManager.getHash().includes(`/plan?show-modal=plan-builder`);
}

export function getMpSourceFromUrl(urlManager: UrlManager = defaultUrlManager): string {
  return urlManager.getQueryStringParam(`mp_source`);
}

export function getCustomAlertIdFromUrl(urlManager: UrlManager = defaultUrlManager): string {
  return urlManager.getQueryStringParam(`alert_id`);
}

export function isDigestMode(urlManager: UrlManager = defaultUrlManager): boolean {
  return urlManager.getPath().startsWith(`/dashboard-digest`);
}

export function isTvMode(urlManager: UrlManager = defaultUrlManager): boolean {
  const tvParam = urlManager.getQueryStringParam(`tv`);
  return tvParam === `true` || tvParam === `1`;
}

export function getResidencyClusterId(clusterCode: ResidencyClusterCountryCode): number {
  const clusterId = RESIDENCY_CLUSTER_IDS_BY_COUNTRY_CODE[clusterCode];
  if (!clusterId) {
    throw new Error(`Unknown cluster code: ${clusterCode}`);
  }
  return clusterId;
}

export function isProduction() {
  return (Object.values(PRODUCTION_DOMAINS) as string[]).includes(getLocation().hostname);
}

export function isStaffEmail(email: string): boolean {
  return !!email && isVerifiedDomain(email, [{domain: PRODUCTION_DOMAINS.US}]);
}

function getQueryStringParam(location: URL, paramName: string) {
  let paramVal = new URLSearchParams(location.search).get(paramName);
  if (paramVal === null) {
    paramVal = new URLSearchParams(location.hash).get(paramName); // wtf dashboards
  }
  return paramVal;
}

/**
 * Useful for ad-hoc trials of experimental features using URL query params (can also optionally enable feature by default in internal dogfood projects)
 * @param location Current location
 * @param urlQueryParam param key to search for in query string or hash. Set to 0 or false to disable
 * @param enableInDogfoodProjectsOrCallback true to force the feature ON for internal projects, equivalent to `() => isCurrentProjectInternal()`
 */
export function isUrlDefinedExperimentalFeatureEnabled(
  location: URL,
  urlQueryParam: string,
  enableInDogfoodProjectsOrCallback: boolean | (() => boolean),
): boolean {
  const enabledInUrl = getQueryStringParam(location, urlQueryParam);
  let enabledInProjects = false;
  if (typeof enableInDogfoodProjectsOrCallback === `boolean`) {
    enabledInProjects = enableInDogfoodProjectsOrCallback ? isCurrentProjectInternal() : false;
  } else {
    enabledInProjects = enableInDogfoodProjectsOrCallback();
  }
  const disabledInUrl = [`false`, `0`].includes(getQueryStringParam(location, urlQueryParam));
  return (enabledInUrl || enabledInProjects) && !(disabledInUrl || DISABLE_EXPERIMENTAL_FEATURES);
}

export function isCurrentProjectInternal() {
  return INTERNAL_PROJECTS.has(parseInt(getProjectId(), 10));
}

export function* cycleWithSkip<T>(arr: T[], skips: T[]) {
  while (true) {
    for (const item of arr) {
      if (skips.find((i) => i === item)) {
        const itemIndex = skips.indexOf(item);
        skips.splice(itemIndex, 1);
      } else {
        yield item;
      }
    }
  }
}

/** Assigns numbers to keys, taking into account previous mappings to attempt to prevent overlap.
 * This is useful for assigning colors to items, as it will skip previously assigned numbers to ensure that the frequency
 * of the number use is minimized
 *
 * @param assignedKeys - previously assigned keys and their ordinal mappings. (their mappings frequency will be used to reduce re-using of ordinal numbers when possible
 * @param newKeys - new keys to be assigned mappings to
 * @param rangeEnd - acts like a modulo: segment keys are sequentially assigned a color index/ordinal from 1 to @maxNumber, resetting to 1 when @maxNumber is reached
 */
export function assignOrdinalNumbersToSegmentKeys(
  assignedKeys: Map<string, number>,
  newKeys: string[],
  rangeEnd: number,
): Map<string, number> {
  const skips = [];
  for (const number of assignedKeys.values()) {
    skips.push(number);
  }
  const rangeArr = range(1, rangeEnd + 1);

  const numberAssignments = new Map(assignedKeys);

  const cycle = cycleWithSkip(rangeArr, skips);

  newKeys.forEach((key) => {
    numberAssignments.set(key, cycle.next().value);
  });

  return numberAssignments;
}

// TODO(sam) move these to `lift-comparison-menu` when we deprecate the old `time-comparison-menu` in favor of lift
export function getDateRangeComparisonOptions(maxDataHistoryDays: number, matchedPreset: {label?: string}): Section[] {
  const presetOptions = RelativeDateRange.getTimeComparisonPresetOptions()
    .filter((option) => {
      const exceedsMaxHistory =
        maxDataHistoryDays &&
        !new RelativeDateRange({type: option.value.type, window: option.value.offset}).isWithinMaxHistoryLimit(
          maxDataHistoryDays,
        );
      return !exceedsMaxHistory;
    })
    .map((option) => {
      return {
        isSelected: matchedPreset?.label === option.label,
        label: option.label,
        value: option.value.offset,
      };
    });

  const customItem = {
    hasCaret: true,
    persistCaret: true,
    label: ({t}) => t(`reports.lift-comparison.previous-period.custom`),
    value: `custom`,
  };

  return [
    {
      items: [...presetOptions, customItem],
    },
  ];
}

// TODO(sam) move these to `lift-comparison-menu` when we deprecate the old `time-comparison-menu` in favor of lift
export function getPresetMatch(comparisonDate?: ComparisonDate) {
  if (!comparisonDate?.offset) {
    return null;
  }
  return RelativeDateRange.getTimeComparisonPresetOptions().find((option) =>
    isEqual(comparisonDate, {offset: option.value.offset}),
  );
}

const orgPersistence = Persistence.createNamespace(LocalStorageKey.OrganizationId);

export function setOrgId({projectId, orgId}: {projectId: string; orgId: number}): void {
  orgPersistence.set(projectId, orgId);
}

export function getOrgId(projectId: string): number | null {
  const orgIdStr = orgPersistence.get(projectId);
  if (orgIdStr) {
    return parseInt(orgIdStr, 10);
  }
  return null;
}

export function getInsightsAggregateCsvExportLimit(project: CurrentProject): number {
  return last(CSV_EXPORT_CHOICES_PROJECT_OVERRIDES[project.id] || INSIGHTS_AGGREGATE_CSV_EXPORT_CHOICES);
}

export function formatSamplingFactor(samplingFactor: number): number {
  return parseFloat((samplingFactor * 100).toFixed(2));
}

export function getReportSamplingFactor(results: BaseQueryResultsType | BaseQueryResultsWithMetaType) {
  if (!results) {
    return 1;
  }
  let reportSamplingFactor = 1;
  if (`meta` in results) {
    if (results.meta && `min_sampling_factor` in results.meta && results.meta.min_sampling_factor) {
      reportSamplingFactor = results.meta.min_sampling_factor;
    } else if (results.metadata && `min_sampling_factor` in results.metadata && results.metadata.min_sampling_factor) {
      reportSamplingFactor = results.metadata.min_sampling_factor;
    } else if (results.min_sampling_factor) {
      reportSamplingFactor = results.min_sampling_factor;
    }
  }
  return reportSamplingFactor;
}
